import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { hasMeaningfulText } from './text-value.utils';

/**
 * Validator to prevent XSS attacks by blocking dangerous characters
 * 
 * Usage in Reactive Forms:
 * this.form = this.fb.group({
 *   name: ['', [Validators.required, NoSpecialCharsValidator()]],
 *   description: ['', [NoSpecialCharsValidator('description')]],
 * });
 * 
 * Display error in template:
 * <small *ngIf="form.get('name')?.hasError('specialChars')" class="p-error">
 *   Caratteri speciali non permessi
 * </small>
 */

/**
 * Validator function for preventing special characters
 * @param mode Validation mode: 'strict' | 'name' | 'alphanumeric' | 'address' | 'description'
 * @returns ValidatorFn
 */
export function NoSpecialCharsValidator(
  mode: 'strict' | 'name' | 'alphanumeric' | 'address' | 'description' | 'free-text' = 'strict'
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Don't validate empty values (use Validators.required)
    }

    const value = control.value.toString();

    // Define patterns based on mode
    let pattern: RegExp;

    switch (mode) {
      case 'name':
        // Only letters, spaces, apostrophes, hyphens, accented characters
        pattern = /^[a-zA-ZÀ-ÿ\s'\-]*$/;
        break;

      case 'alphanumeric':
        // Only letters and numbers
        pattern = /^[a-zA-Z0-9]*$/;
        break;

      case 'address':
        // Letters, numbers, spaces, commas, periods, hyphens, slashes
        pattern = /^[a-zA-Z0-9\s,.\-'\/]*$/;
        break;

      case 'description':
        // Letters, numbers, spaces, common punctuation
        pattern = /^[a-zA-Z0-9\s\.,;:!?@#$%\^\-_()[\]{}+=\n\r]*$/;
        break;

      case 'free-text': {
        // Smart XSS detection: permette < > & ' " come caratteri singoli,
        // li blocca solo quando formano strutture HTML (tag, event handler, URI pericolosi).
        // Allineato alla filosofia BE @NoXss (pattern blacklist, non char blacklist).
        //
        // Blocca:
        //   <script>  <b>  </div>  onclick=  javascript:  &#60;  %3C
        // Permette:
        //   valore > 0   a < b   Rossi & Figli   dell'accordo   "citazione"
        const xssPattern = /(<[a-zA-Z!]|<\/)|on\w+\s*=|javascript\s*:|vbscript\s*:|data\s*:text\/html|&#\d|%3[cCeE]|expression\s*\(/i;
        if (xssPattern.test(value)) {
          return {
            specialChars: {
              value,
              message: 'Il testo non può contenere tag HTML o script',
            },
          };
        }
        return null;
      }

      default:
        // Strict mode - blocks < > " ' & / \
        pattern = /^[a-zA-Z0-9\s\.,;:!?@#$%\^\-_()[\]{}+=]*$/;
    }

    // Check if value contains disallowed characters
    const isValid = pattern.test(value);

    if (!isValid) {
      // Find which characters are actually blocked by the active mode
      const blockedByMode: Partial<Record<typeof mode, RegExp>> = {
        'strict':       /[<>"'&\\\/]/g,
        'name':         /[^a-zA-Z\u00C0-\u00FF\s'\-]/g,
        'alphanumeric': /[^a-zA-Z0-9]/g,
        'address':      /[^a-zA-Z0-9\s,.\-'\//]/g,
        'description':  /[^a-zA-Z0-9\s\.,;:!?@#$%\^\-_()[\]{}+=\n\r]/g,
      };
      const dangerousChars = value.match(blockedByMode[mode] ?? /[<>"'&\\\/]/g);
      
      return {
        specialChars: {
          value: value,
          message: dangerousChars 
            ? `Caratteri non permessi: ${[...new Set(dangerousChars)].join(' ')}` 
            : 'Caratteri speciali non permessi',
        },
      };
    }

    return null;
  };
}

export function RequiredTrimmedValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value === null || value === undefined) {
      return { required: true };
    }

    if (typeof value === 'string') {
      return hasMeaningfulText(value) ? null : { required: true };
    }

    if (Array.isArray(value)) {
      return value.length > 0 ? null : { required: true };
    }

    return null;
  };
}

/**
 * Validator to check for dangerous XSS characters specifically
 * More strict than NoSpecialCharsValidator
 */
export function NoXssValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const value = control.value.toString();
    const dangerousPattern = /[<>"'&\\]/;

    if (dangerousPattern.test(value)) {
      const foundChars = value.match(/[<>"'&\\]/g);
      return {
        xss: {
          value: value,
          message: `Caratteri pericolosi rilevati: ${[...new Set(foundChars || [])].join(' ')}`,
        },
      };
    }

    return null;
  };
}

/**
 * Escapes HTML special characters in a string to prevent HTML injection
 * when interpolating user-provided values inside confirm dialog messages
 * (which PrimeNG renders via [innerHTML]).
 *
 * Usage:
 *   message: `Vuoi rimuovere <strong>${escapeHtml(businessName)}</strong>?`
 */
export function escapeHtml(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Helper function to check if control has special chars error
 */
export function hasSpecialCharsError(control: AbstractControl | null): boolean {
  return !!(control && control.hasError('specialChars'));
}

/**
 * Helper function to get special chars error message
 */
export function getSpecialCharsErrorMessage(control: AbstractControl | null): string {
  if (!control || !control.hasError('specialChars')) {
    return '';
  }

  const error = control.getError('specialChars');
  return error.message || 'Caratteri speciali non permessi';
}
